/**
 * @file MineralBulkPricingModal.js
 * @version 2.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-09
 * @modified 2026-07-17
 * @description Bulk version of the mineral pricing edit — set cost, haulage and margin for
 *              every selected mineral row in one go. Cost is the headline field: a changed
 *              cost is written to the inventory item and pushed to Xero (unchanged costs are
 *              never re-pushed). Haulage / margin are local overrides; sale price
 *              (cost + haulage + margin) is shown live per row. Notes / code / description
 *              stay editable only in the single-row modal. Only changed rows are saved.
 *              Saving streams NDJSON progress from the server so a live progress bar + per-row
 *              status are shown while cost pushes to Xero one row at a time, then a summary.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContactModal.css';
import './MineralPricing.css';
import Input from './Input';
import LoadingBar from './LoadingBar';
import { formatCurrency } from '../utils/currencyFormatter';
import { useSlideOutButton } from './SlideOutButton';

// Normalise an editable field to a comparable string so we can detect real changes
// (blank margin === POA, so '' and null must compare equal).
const norm = (v) => (v === null || v === undefined) ? '' : String(v).trim();

function MineralBulkPricingModal({ minerals, onClose, onSave }) {
  // phase: 'edit' (form) -> 'running' (streaming) -> 'done' (summary)
  const [phase, setPhase] = useState('edit');
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');

  // streaming progress state
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const [rowStatus, setRowStatus] = useState({}); // { [id]: { status, costPushed, xeroPushFailed, localChanged, skipped, error } }
  const [submittedRows, setSubmittedRows] = useState([]); // rows captured at submit for the status list
  const [summary, setSummary] = useState(null);

  useSlideOutButton([phase]);

  const [rows, setRows] = useState(() => (minerals || []).map(m => ({
    id: m.id,
    inventory_item_id: m.inventory_item_id,
    item_code: m.item_code,
    item_name: m.item_name,
    // editable fields (strings for the inputs)
    cost: m.cost != null ? String(m.cost) : '',
    haulage: m.haulage != null ? String(m.haulage) : '',
    margin: m.margin != null ? String(m.margin) : '',
    // originals for change detection
    orig_cost: m.cost != null ? String(m.cost) : '',
    orig_haulage: m.haulage != null ? String(m.haulage) : '',
    orig_margin: m.margin != null ? String(m.margin) : ''
  })));

  useEffect(() => {
    document.body.classList.add('modal-open');
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) contentWrapper.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.remove('modal-open');
    };
  }, []);

  const setRow = (id, field, value) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, [field]: value } : r)));
    setError('');
  };

  const handleClose = () => {
    if (phase === 'running') return; // don't allow closing mid-Xero-push
    setClosing(true);
    setTimeout(() => onClose(), 200);
  };

  const costChanged = (r) => norm(r.cost) !== norm(r.orig_cost);
  const isChanged = (r) => costChanged(r)
    || norm(r.haulage) !== norm(r.orig_haulage)
    || norm(r.margin) !== norm(r.orig_margin);

  const changedCount = rows.filter(isChanged).length;
  const costChangedCount = rows.filter(costChanged).length;

  // Live sale price for a row: cost + haulage + margin. Blank margin = POA.
  const salePrice = (r) => {
    const marginN = norm(r.margin) === '' ? null : parseFloat(r.margin);
    if (marginN == null || isNaN(marginN)) return null;
    const costN = norm(r.cost) === '' ? 0 : (parseFloat(r.cost) || 0);
    const haulageN = norm(r.haulage) === '' ? 0 : (parseFloat(r.haulage) || 0);
    return costN + haulageN + marginN;
  };

  const handleEvent = (evt) => {
    if (evt.type === 'progress') {
      setProgress(evt.percentage || 0);
      setProgressMsg(evt.currentName && evt.processed < evt.total
        ? `Updating ${evt.processed + 1} of ${evt.total}: ${evt.currentName}`
        : `${evt.processed} of ${evt.total} processed`);
    } else if (evt.type === 'item_done') {
      setRowStatus(prev => ({
        ...prev,
        [evt.id]: {
          status: evt.skipped ? 'skipped' : 'done',
          costPushed: evt.costPushed,
          xeroPushFailed: evt.xeroPushFailed,
          localChanged: evt.localChanged
        }
      }));
    } else if (evt.type === 'item_failed') {
      setRowStatus(prev => ({ ...prev, [evt.id]: { status: 'failed', error: evt.error } }));
    } else if (evt.type === 'result') {
      setProgress(100);
      setSummary(evt);
      setProgressMsg('Complete');
      setPhase('done');
    } else if (evt.type === 'error') {
      setError(evt.error || 'Bulk update failed');
      setPhase('done');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const changed = rows.filter(isChanged);
    const items = changed.map(r => ({
      id: r.id,
      inventory_item_id: r.inventory_item_id,
      item_code: r.item_code,
      item_name: r.item_name,
      cost: norm(r.cost) === '' ? null : parseFloat(r.cost),
      haulage: norm(r.haulage) === '' ? null : parseFloat(r.haulage),
      margin: norm(r.margin) === '' ? null : parseFloat(r.margin)
    }));
    if (items.length === 0) { setError('No changes to save'); return; }

    // seed the status list + mark all as pending
    setSubmittedRows(changed.map(r => ({ id: r.id, item_code: r.item_code, item_name: r.item_name })));
    setRowStatus(Object.fromEntries(changed.map(r => [r.id, { status: 'pending' }])));
    setProgress(0);
    setProgressMsg(`Starting — ${items.length} to update...`);
    setPhase('running');

    try {
      const res = await fetch(`${API_URL}/api/pricing/minerals/bulk-pricing`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items })
      });
      if (!res.ok || !res.body) {
        let msg = `HTTP ${res.status}`;
        try { const d = await res.json(); msg = d.error || msg; } catch { /* not json */ }
        throw new Error(msg);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf('\n')) !== -1) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line); } catch { continue; }
          handleEvent(evt);
        }
      }
      // if the stream ended without a result event, still land on the summary
      setPhase(prev => (prev === 'running' ? 'done' : prev));
    } catch (err) {
      console.error('Error bulk-saving mineral pricing:', err);
      setError(err.message);
      setPhase('done');
    }
  };

  const failedCount = submittedRows.filter(r => rowStatus[r.id]?.status === 'failed').length;

  // Per-row status pill for the running / done phases.
  const statusPill = (st) => {
    if (!st || st.status === 'pending') return <span className="mineral-status-pill status-pending">Updating…</span>;
    if (st.status === 'failed') return <span className="mineral-status-pill status-failed" title={st.error}>Failed</span>;
    if (st.status === 'skipped') return <span className="mineral-status-pill status-unsynced">No change</span>;
    if (st.xeroPushFailed) return <span className="mineral-status-pill status-warning">Saved · Xero failed</span>;
    if (st.costPushed) return <span className="mineral-status-pill status-done">Updated · Xero</span>;
    return <span className="mineral-status-pill status-done">Updated</span>;
  };

  const isRunning = phase === 'running';
  const isDone = phase === 'done';

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`modal-content mineral-bulk-modal modal-lg ${closing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h4 className="modal-title">
            {phase === 'edit'
              ? `Edit Prices — ${rows.length} Selected Mineral${rows.length === 1 ? '' : 's'}`
              : (isDone ? 'Bulk Price Update — Summary' : 'Updating Mineral Prices…')}
          </h4>
          <button className="btn-icon close-btn" onClick={handleClose} title="Close" disabled={isRunning}>
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        {phase === 'edit' ? (
          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ padding: 'var(--spacing-md) var(--spacing-xl) var(--spacing-xl)' }}>
              {error && (
                <div className="error-message">
                  <span className="material-symbols-rounded">error</span>
                  {error}
                </div>
              )}

              <div className="mineral-bulk-hint">
                <span className="material-symbols-rounded">info</span>
                <span>
                  Edit cost, haulage or margin. A changed <strong>cost</strong> is pushed to Xero
                  (unchanged costs are left alone). Blank margin means POA. Notes, code and
                  description are edited from the single-row modal. <strong>{changedCount}</strong> of {rows.length} changed
                  {costChangedCount > 0 ? ` (${costChangedCount} cost → Xero)` : ''}.
                </span>
              </div>

              <div className="mineral-bulk-list">
                <div className="mineral-bulk-row mineral-bulk-head">
                  <div>Product</div>
                  <div>Cost (£)</div>
                  <div>Haulage (£)</div>
                  <div>Margin (£)</div>
                  <div className="mineral-bulk-num-col">Sale price</div>
                </div>
                {rows.map(r => {
                  const price = salePrice(r);
                  return (
                    <div key={r.id} className={`mineral-bulk-row ${isChanged(r) ? 'mineral-bulk-changed' : ''}`}>
                      <div className="mineral-bulk-prod">
                        {r.item_code && <span className="product-code">{r.item_code}</span>}
                        <span className="mineral-bulk-name" title={r.item_name}>{r.item_name}</span>
                      </div>
                      <Input
                        type="number"
                        step="0.01"
                        value={r.cost}
                        onChange={(e) => setRow(r.id, 'cost', e.target.value)}
                        placeholder="0.00"
                      />
                      <Input
                        type="number"
                        step="1"
                        value={r.haulage}
                        onChange={(e) => setRow(r.id, 'haulage', e.target.value)}
                        placeholder="e.g. 60"
                      />
                      <Input
                        type="number"
                        step="1"
                        value={r.margin}
                        onChange={(e) => setRow(r.id, 'margin', e.target.value)}
                        placeholder="POA"
                      />
                      <div className="mineral-bulk-num-col mineral-bulk-price">
                        {price != null ? formatCurrency(price) : 'POA'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer" style={{ position: 'absolute', bottom: '0', left: '0', right: '0' }}>
              <button type="submit" className="btn-slideout btn-slideout-primary" disabled={changedCount === 0}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">save</span>
                </div>
                <span className="btn-label">{`Save ${changedCount} Change${changedCount === 1 ? '' : 's'}`}</span>
              </button>
              <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleClose}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Cancel</span>
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="modal-body" style={{ padding: 'var(--spacing-md) var(--spacing-xl) var(--spacing-xl)' }}>
              <div className="mineral-bulk-progress-wrap">
                <LoadingBar progress={progress} message={progressMsg} indeterminate={false} />
              </div>

              {isDone && (
                error ? (
                  <div className="error-message">
                    <span className="material-symbols-rounded">error</span>
                    {error}
                  </div>
                ) : summary && (
                  <div className="mineral-bulk-summary">
                    <div className="mineral-bulk-summary-row">
                      <span className="material-symbols-rounded" style={{ color: 'var(--color-success)' }}>check_circle</span>
                      <span><strong>{summary.updated}</strong> product{summary.updated === 1 ? '' : 's'} updated</span>
                    </div>
                    <div className="mineral-bulk-summary-row">
                      <span className="material-symbols-rounded" style={{ color: 'var(--color-info)' }}>sync</span>
                      <span><strong>{summary.costPushed}</strong> cost change{summary.costPushed === 1 ? '' : 's'} pushed to Xero</span>
                    </div>
                    {summary.xeroWarnings > 0 && (
                      <div className="mineral-bulk-summary-row">
                        <span className="material-symbols-rounded" style={{ color: 'var(--color-warning)' }}>warning</span>
                        <span><strong>{summary.xeroWarnings}</strong> saved locally but failed to reach Xero</span>
                      </div>
                    )}
                    {failedCount > 0 && (
                      <div className="mineral-bulk-summary-row">
                        <span className="material-symbols-rounded" style={{ color: 'var(--color-error)' }}>cancel</span>
                        <span><strong>{failedCount}</strong> row{failedCount === 1 ? '' : 's'} failed</span>
                      </div>
                    )}
                  </div>
                )
              )}

              <div className="mineral-bulk-list mineral-bulk-status-listing">
                {submittedRows.map(r => (
                  <div key={r.id} className="mineral-bulk-status-row">
                    <div className="mineral-bulk-prod">
                      {r.item_code && <span className="product-code">{r.item_code}</span>}
                      <span className="mineral-bulk-name" title={r.item_name}>{r.item_name}</span>
                    </div>
                    <div className="mineral-bulk-status-cell">{statusPill(rowStatus[r.id])}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer" style={{ position: 'absolute', bottom: '0', left: '0', right: '0' }}>
              <button
                type="button"
                className="btn-slideout btn-slideout-primary"
                onClick={() => onSave()}
                disabled={isRunning}
              >
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded ${isRunning ? 'spinning' : ''}`}>
                    {isRunning ? 'sync' : 'done'}
                  </span>
                </div>
                <span className="btn-label">{isRunning ? 'Updating...' : 'Done'}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default MineralBulkPricingModal;
